// src/components/InKindInputForm.js
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  getCitiesByState,
  getInputsCategories,
  getLocalityByState,
  getmarketsByState,
  getStates,
} from "../api";
import { api_url } from "../constant";
import AppBar from "./appBar";
import Client from "../utils/client";
import ReactGA from "react-ga4";

function InKindInputForm({ contributionType = "donate" }) {
  ReactGA.send({
    hitType: "pageview",
    page: window.location.pathname,
    title: `In-Kind Input ${contributionType === "donate" ? "Donation" : "Request"}`,
  });

  const [images, setImages] = useState([]);
  const [media, setMedia] = useState([]);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    reset,
  } = useForm();

  const { data: categories } = useQuery({
    queryKey: ["inputsCategories"],
    queryFn: () => getInputsCategories(),
    initialData: [],
  });

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: () => getStates(),
    initialData: [],
  });

  const { data: cities, refetch: citiesRefetch } = useQuery({
    queryKey: ["cities"],
    queryFn: () => getCitiesByState(getValues().state?.value),
    initialData: [],
    enabled: false,
  });

  const { data: markets, refetch: marketRefetch } = useQuery({
    queryKey: ["markets"],
    queryFn: () => getmarketsByState(getValues().state?.value),
    initialData: [],
    enabled: false,
  });

  const { data: locality, refetch: localityRefetch } = useQuery({
    queryKey: ["locality"],
    queryFn: () => getLocalityByState(getValues().state?.value),
    initialData: [],
    enabled: false,
  });

  const onSubmit = (data) => post(data);

  const post = async (data) => {
    // Validation
    if (!data.category_id || !data.category_id.value) {
      toast("يرجى اختيار المدخل", { type: "error" });
      return;
    }
    if (!data.state || !data.state.value) {
      toast("يرجى اختيار الولاية", { type: "error" });
      return;
    }
    if (!data.locality || !data.locality.value) {
      toast("يرجى اختيار المحلية", { type: "error" });
      return;
    }
    if (!data.name) {
      toast("يرجى كتابة اسم المنتج", { type: "error" });
      return;
    }
    if (!data.description) {
      toast("يرجى كتابة الوصف", { type: "error" });
      return;
    }
    if (!data.location) {
      toast("يرجى تحديد الموقع التفصيلي", { type: "error" });
      return;
    }

    const _data = JSON.stringify({
      category_id: data.category_id?.value,
      name: data.name,
      description: data.description,
      location: data.location,
      locality: data.locality?.value.toString() || null,
      market: data.market?.value.toString() || null,
      state: data.state?.value.toString(),
      // Optional technical fields
      ingredients: data.ingredients || null,
      application: data.application || null,
      dose: data.dose || null,
      cautions: data.cautions || null,
      storing: data.storing || null,
      quality_certificate: data.quality_certificate || null,
      price: 0, // Always 0 for in-kind
      type: contributionType === "donate" ? "sell" : "buy", // Map to offerType
    });

    try {
      const response = await Client.post(api_url + "/inputs/in-kind", _data, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.id) {
        const inputId = response.data.id;

        // Upload images only for donations
        if (contributionType === "donate" && images.length > 0) {
          await uploadFiles(inputId);
        }

        const successMessage = contributionType === "donate"
          ? "تم نشر التبرع بالمدخل بنجاح"
          : "تم نشر طلب المدخل بنجاح";
        toast(successMessage, { type: "success" });

        setTimeout(() => {
          window.history.go(-1);
        }, 1500);
      } else {
        throw new Error("فشل في إنشاء المساهمة العينية");
      }
    } catch (error) {
      console.error("Error:", error);
      toast("الرجاء التأكد من البيانات", { type: "error" });
    }
  };

  const uploadFiles = async (inputId) => {
    const formData = new FormData();
    images.forEach((image, index) => {
      formData.append("images", image);
    });

    try {
      const response = await fetch(`${api_url}/files/upload-images/input/${inputId}`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });

      const result = await response.json();

      if (response.status > 201) {
        throw new Error(result.message || "فشل في تحميل الصور");
      }

      toast("تم تحميل الصور بنجاح", { type: "success" });
    } catch (error) {
      console.error("Error uploading files:", error);
      toast("فشل في تحميل الصور", { type: "error" });
    }
  };

  // Handle file upload
  const handleImageUpload = (file) => {
    if (file) {
      const maxSizeMB = 10;
      if (file.size > maxSizeMB * 1024 * 1024) {
        toast.error(`حجم الملف يتجاوز ${maxSizeMB} ميجابايت`);
        return;
      }

      if (images.length >= 5) {
        toast.error("يمكنك تحميل حتى 5 ملفات فقط");
        return;
      }

      setImages((prevFiles) => {
        if (prevFiles.length < 5) {
          return [...prevFiles, file];
        }
        return prevFiles;
      });

      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        setMedia((prevMedia) => [...prevMedia, e.target.result]);
      };
      fileReader.readAsDataURL(file);
    }
  };

  // Handle file removal
  const removeImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    setMedia((prevMedia) => prevMedia.filter((_, i) => i !== index));
  };

  return (
    <div className="items-center justify-center w-screen min-h-screen overflow-x-hidden" dir="rtl">
      <AppBar />
      <div className="h-10" />
      
      {/* Header */}
      <div className="flex flex-row justify-between px-40 font-bold text-primary text-[18px]">
        <div
          className="flex justify-center items-center cursor-pointer"
          onClick={() => window.history.go(-1)}
        >
          <svg
            className="w-7 text-primary"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          <p>عودة</p>
        </div>
        <div className="text-black">
          {contributionType === "donate" ? "تبرع بمدخل زراعي" : "طلب مدخل زراعي"}
        </div>
      </div>

      <div className="h-10"></div>

      {/* Info Box */}
      <div className="px-40 mb-6">
        <div className={`p-4 rounded-lg ${
          contributionType === "donate" 
            ? "bg-green-50 border border-green-200" 
            : "bg-blue-50 border border-blue-200"
        }`}>
          <p className={`font-bold ${
            contributionType === "donate" ? "text-green-800" : "text-blue-800"
          }`}>
            {contributionType === "donate" 
              ? "💚 شارك بمدخلاتك الزراعية مع من يحتاجها" 
              : "💙 اطلب المدخلات الزراعية التي تحتاجها"
            }
          </p>
          <p className={`text-sm mt-1 ${
            contributionType === "donate" ? "text-green-700" : "text-blue-700"
          }`}>
            {contributionType === "donate"
              ? "ساهم في دعم المزارعين المحتاجين من خلال التبرع بالبذور، الأسمدة، المبيدات، وغيرها"
              : "احصل على الدعم من المانحين لتغطية احتياجاتك من المدخلات الزراعية"
            }
          </p>
        </div>
      </div>

      <div className="flex flex-col">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col items-center justify-center"
        >
          {/* Category Selection */}
          <div className="flex flex-col w-2/6">
            <ReactSelect
              className="border-primary border-[1px] rounded-md"
              options={categories.map((e) => {
                return { label: e.name, value: e.id };
              })}
              value={getValues().category_id}
              onChange={(value) =>
                reset({ ...getValues(), category_id: value })
              }
              hideSelectedOptions={false}
              closeMenuOnSelect={true}
              isRtl
              placeholder={
                <span className="font-bold after:content-['*'] after:mr-1 after:text-red-500">
                  المدخل
                </span>
              }
            />
          </div>
          <div className="h-5" />

          {/* Location Selection */}
          <div className="flex flex-col-reverse lg:flex-row lg:gap-5 w-full justify-center items-center">
            <div className="flex flex-col w-2/6 lg:w-1/6">
              <ReactSelect
                className="border-primary border-[1px] rounded-md"
                options={states.map((e) => {
                  return { label: e.name, value: e.id };
                })}
                value={getValues().state}
                onChange={(value) => {
                  setValue("state", value);
                  citiesRefetch();
                  marketRefetch();
                  localityRefetch();
                }}
                hideSelectedOptions={false}
                closeMenuOnSelect={true}
                isRtl
                placeholder={
                  <span className="font-bold after:content-['*'] after:mr-1 after:text-red-500">
                    الولاية
                  </span>
                }
              />
            </div>
          </div>
          <div className="h-5" />

          <div className="flex flex-col lg:flex-row lg:gap-5 w-full justify-center items-center">
            <div className="flex flex-col w-2/6 lg:w-1/6">
              <ReactSelect
                className="border-primary border-[1px] rounded-md"
                options={markets.map((e) => {
                  return { label: e.name, value: e.id };
                })}
                value={getValues().market}
                onChange={(value) => reset({ ...getValues(), market: value })}
                hideSelectedOptions={false}
                closeMenuOnSelect={true}
                isRtl
                placeholder="السوق"
                isDisabled={markets.length === 0}
              />
            </div>

            <div className="h-5" />

            <div className="flex flex-col w-2/6 lg:w-1/6">
              <ReactSelect
                className="border-primary border-[1px] rounded-md"
                options={locality.map((e) => {
                  return { label: e.name, value: e.id };
                })}
                value={getValues().locality}
                onChange={(value) => {
                  reset({ ...getValues(), locality: value });
                }}
                hideSelectedOptions={false}
                closeMenuOnSelect={true}
                isRtl
                isDisabled={locality.length === 0}
                placeholder={
                  <span className="font-bold after:content-['*'] after:mr-1 after:text-red-500">
                    المحلية
                  </span>
                }
              />
            </div>
          </div>

          <div className="h-5" />

          {/* Detailed Location Input */}
          <input
            {...register("location", { required: true })}
            dir="rtl"
            className="border h-10 px-2 mb-5 rounded-md w-96 border-primary"
            placeholder="الموقع التفصيلي (القرية، المنطقة، المعلم...)*"
          />

          {/* Product Name */}
          <input
            {...register("name", { required: true })}
            dir="rtl"
            className="border h-10 px-2 mb-5 rounded-md w-96 border-primary"
            placeholder="اسم المنتج*"
          />

          {/* Description */}
          <textarea
            {...register("description", { required: true })}
            className="border-[1px] h-32 resize-none p-3 mb-5 rounded-md w-96 break-words border-primary"
            multiple
            placeholder={
              contributionType === "donate"
                ? "وصف المدخل المتبرع به، مثلا: (بذور قمح عالية الجودة، مناسبة للمناطق الشمالية)*"
                : "وصف المدخل المطلوب والسبب، مثلا: (أحتاج لأسمدة نيتروجينية لمحصول الذرة بسبب نقص الخصوبة)*"
            }
            dir="rtl"
          />

          {/* Technical Details - Only for donations */}
          {contributionType === "donate" && (
            <>
              <div className="flex w-[45%] gap-5">
                <textarea
                  {...register("ingredients")}
                  dir="rtl"
                  className="border h-14 resize-none px-2 mb-5 rounded-md w-[50%] border-primary"
                  placeholder="مكونات المنتج (اختياري)"
                />
                <textarea
                  {...register("application")}
                  dir="rtl"
                  className="border h-14 px-2 resize-none mb-5 rounded-md w-[50%] border-primary"
                  placeholder="كيفية الاستخدام (اختياري)"
                />
              </div>

              <div className="flex w-[45%] gap-5">
                <textarea
                  {...register("dose")}
                  dir="rtl"
                  className="border h-14 resize-none px-2 mb-5 rounded-md w-[50%] border-primary"
                  placeholder="الجرعة أو الكمية المناسبة (اختياري)"
                />
                <textarea
                  {...register("cautions")}
                  dir="rtl"
                  className="border h-14 px-2 resize-none mb-5 rounded-md w-[50%] border-primary"
                  placeholder="إرشادات السلامة (اختياري)"
                />
              </div>

              <div className="flex w-[45%] gap-5">
                <textarea
                  {...register("storing")}
                  dir="rtl"
                  className="border h-14 resize-none px-2 mb-5 rounded-md w-[50%] border-primary"
                  placeholder="تواريخ الاستخدام والتخزين (اختياري)"
                />
                <textarea
                  {...register("quality_certificate")}
                  dir="rtl"
                  className="border h-14 px-2 resize-none mb-5 rounded-md w-[50%] border-primary"
                  placeholder="شهادة الجودة (اختياري)"
                />
              </div>
            </>
          )}

          {/* Additional Info for Requests */}
          {contributionType === "need" && (
            <div className="w-96 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
              <div className="flex items-start">
                <div className="text-blue-500 ml-2 mt-1">💡</div>
                <div>
                  <h4 className="font-bold text-blue-800 text-sm">نصيحة</h4>
                  <p className="text-blue-700 text-xs mt-1">
                    اذكر في الوصف سبب حاجتك للمدخل الزراعي وأي معلومات إضافية تساعد المانحين على فهم وضعك.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Image Upload - Only for donations */}
          {contributionType === "donate" && (
            <>
              <div className="w-96 h-[20vh] border-primary border-[1px] bg-gray-50 rounded-md flex justify-center items-center text-gray-500">
                <input
                  type="file"
                  id="file"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  accept="image/*"
                />
                <label
                  htmlFor="file"
                  className="font-bold before:mr-1 before:text-red-500 cursor-pointer"
                >
                  إرفاق صور (اختياري)
                </label>
              </div>
              <div className="h-5" />

              {/* Image Previews */}
              {media.length > 0 && (
                <div className="flex flex-row gap-3">
                  {media.map((src, index) => (
                    <div key={index} className="relative">
                      <img
                        src={src}
                        className="w-16 h-16 object-cover border border-gray-300 rounded"
                        alt="Preview"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-sm"
                        onClick={() => removeImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Submit Button */}
          <input
            type="submit"
            className={`h-12 text-white rounded-md w-60 font-bold cursor-pointer ${
              contributionType === "donate" 
                ? "bg-green-600 hover:bg-green-700" 
                : "bg-blue-600 hover:bg-blue-700"
            } transition-colors`}
            value={contributionType === "donate" ? "تبرع بالمدخل" : "اطلب المدخل"}
          />
        </form>
      </div>
      
      <div className="h-20" />
    </div>
  );
}

export default InKindInputForm;